import { Badge, NavLink, Text, Tooltip } from '@mantine/core'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'

import { cn } from '@/utils/cn'

export type DetailsNavSection = {
  id: string
  label: string
  /** Shown as a pill; omitted for uncounted sections and hidden when 0. */
  count?: number
}

type Props = {
  sections: DetailsNavSection[]
  activeId: string | null
  collapsed: boolean
  onToggleCollapsed: () => void
  onSelectSection: (id: string) => void
}

const NAV_LIST_ID = 'clinical-summary-section-nav-list'

// Table-of-contents rail for the Details tab: one entry per section with a count pill, the
// active entry tracking the scroll position. Collapses to a chevron-only rail. Sits beside the
// panel's scroll container, so it stays put while the content scrolls.
export const ClinicalSummaryDetailsNav = ({
  sections,
  activeId,
  collapsed,
  onToggleCollapsed,
  onSelectSection,
}: Props) => (
  <nav
    aria-label="Clinical summary sections"
    className={cn(
      'flex h-full shrink-0 flex-col transition-[width] duration-200',
      collapsed ? 'w-[40px]' : 'w-[220px]',
    )}
  >
    <div
      className={cn(
        'mb-1 flex items-center',
        collapsed ? 'justify-center' : 'justify-between pl-3',
      )}
    >
      {!collapsed && (
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          Sections
        </Text>
      )}
      <Tooltip label={collapsed ? 'Show section navigation' : 'Hide section navigation'} withArrow>
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Show section navigation' : 'Hide section navigation'}
          aria-expanded={!collapsed}
          aria-controls={NAV_LIST_ID}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#e2e8f0] bg-white text-slate-500 transition hover:border-[#bfdbfe] hover:bg-[#eff6ff] hover:text-[#2563eb]"
        >
          {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
        </button>
      </Tooltip>
    </div>

    {!collapsed && (
      <div id={NAV_LIST_ID} className="min-h-0 flex-1 overflow-y-auto">
        {sections.map((section) => (
          <NavLink
            key={section.id}
            component="button"
            type="button"
            label={section.label}
            active={section.id === activeId}
            aria-current={section.id === activeId ? 'location' : undefined}
            onClick={() => onSelectSection(section.id)}
            rightSection={
              section.count ? (
                <Badge size="sm" variant="light" color="gray">
                  {section.count}
                </Badge>
              ) : undefined
            }
            styles={{ label: { fontSize: 'var(--mantine-font-size-sm)' } }}
            className="rounded-md"
          />
        ))}
      </div>
    )}
  </nav>
)
